import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { FitnessResponse } from '@painpal/contracts';
import { useState } from 'react';
import { invalidateTraining, type SportFilter } from '../../api/keys';
import { useFitness, useFitnessPolling, useStravaStatus } from '../../api/queries';
import { Eyebrow } from '../../components/Eyebrow';
import { Headline } from '../../components/Headline';
import { EmptyState, ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { rangeStart, type FitnessRange } from '../../lib/dates';
import { plural } from '../../lib/plural';
import { importedCount } from '../../lib/strava';
import { useIsPhone } from '../../ui/useMediaQuery';
import { useOnTransition } from '../../ui/useOnTransition';
import { FitnessChartPanel } from './FitnessChartPanel';
import { defaultFitnessRange, todayStats } from './fitnessStats';
import { fitnessEyebrow, fitnessHeadline } from './headline';
import { LatestRides } from './LatestRides';
import { SportFilterRow } from './SportFilter';
import { StravaImportCard } from './StravaImportCard';
import { Ticker } from './Ticker';
import { TimeInZoneCard } from './TimeInZoneCard';
import { TodayStrip } from './TodayStrip';
import { TodaysPlanStrip } from './TodaysPlanStrip';
import { WeekSnapshotCard } from './WeekSnapshotCard';
import styles from './fitness.module.css';

interface HeaderProps { today: string; data: FitnessResponse; imported: number | null; importing: boolean; rebuilding: boolean }

function FitnessHeader({ today, data, imported, importing, rebuilding }: HeaderProps) {
  const stats = todayStats(data.days);
  const { first, second, note } = fitnessHeadline({ stats, today, importing, rebuilding, historyStart: data.historyStart });
  return (
    <header className={styles.header}>
      <div className={`page-heading ${styles.headerLeft}`}>
        <Eyebrow>{fitnessEyebrow(today, stats === null ? null : stats.zone, imported)}</Eyebrow>
        <Headline first={first} second={second} />
        {note !== null && <p className={styles.headlineNote}>{note}</p>}
      </div>
      {stats !== null && <TodayStrip stats={stats} />}
    </header>
  );
}

function EmptyFitness() {
  return (
    <section className={styles.panel}>
      <EmptyState title="NO ACTIVITIES YET" body="Fitness, Fatigue and Form appear after your first activity with Load.">
        <div className={styles.emptyActions}>
          <Link to="/activities" search={{ upload: true }} className="btn btn-ink">+ ADD ACTIVITY</Link>
          <Link to="/onboarding/rides" className="btn btn-outline">CONNECT STRAVA</Link>
        </div>
      </EmptyState>
    </section>
  );
}

interface LoadedProps {
  data: FitnessResponse; today: string; imported: number | null; importing: boolean; rebuilding: boolean; range: FitnessRange; onRange: (r: FitnessRange) => void; pollZones: () => number | false;
  sport: SportFilter;
}

function FitnessLoaded({ data, today, imported, importing, rebuilding, range, onRange, pollZones, sport }: LoadedProps) {
  const isPhone = useIsPhone();
  // A sport the athlete has no activities in (an old link, or its last activity deleted) shows ALL.
  const shown = sport !== 'all' && data.sports.includes(sport) ? sport : 'all';
  const count = data.ridesMissingFtp;
  return (
    <>
      <FitnessHeader today={today} data={data} imported={imported} importing={importing} rebuilding={rebuilding} />
      {count > 0 && (
        <p className={`alert-amber ${styles.banner}`}>
          {count} {plural(count, 'RIDE HAS', 'RIDES HAVE')} NO LOAD BECAUSE NO FTP COVERS ITS DATE ·{' '}
          <Link to="/settings">SET FTP →</Link>
        </p>
      )}
      <TodaysPlanStrip today={today} />
      {data.days.length === 0 ? <EmptyFitness /> : (
        <>
          {isPhone && data.sports.length > 0 && <SportFilterRow sport={shown} sports={data.sports} phone />}
          <FitnessChartPanel
            days={data.days} forecast={data.forecast} plannedDays={data.forecastPlannedDays} range={range} onRange={onRange}
            sport={shown} sports={data.sports}
          />
          <WeekSnapshotCard today={today} poll={pollZones} sport={shown} />
          <TimeInZoneCard range={range} today={today} poll={pollZones} sport={shown} sports={data.sports} />
        </>
      )}
    </>
  );
}

export function FitnessPage({ today, sport = 'all' }: { today: string; sport?: SportFilter }) {
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  const [range, setRange] = useState<FitnessRange>(() => defaultFitnessRange(isPhone));
  const strava = useStravaStatus();
  const backfill = strava.data?.backfill;
  const importing = backfill?.status === 'running';
  const polling = useFitnessPolling(importing);
  const fitness = useFitness(rangeStart(range, today), today, polling.fitness);
  // One last read once the server says the rebuild is done, so every chart settles on its result.
  useOnTransition(fitness.data?.rebuilding === true, (wasRebuilding) => { if (wasRebuilding) void invalidateTraining(queryClient); });
  const imported = backfill ? importedCount(backfill) : null;
  return (
    <>
      <Ticker />
      <div className="page">
        {fitness.isPending && <LoadingBlock label="LOADING YOUR FITNESS" height={560} />}
        {fitness.isError && <ErrorPanel error={fitness.error} onRetry={() => void fitness.refetch()} />}
        {fitness.isSuccess && <FitnessLoaded data={fitness.data} today={today} imported={imported} importing={importing} rebuilding={fitness.data.rebuilding && !polling.rebuildTimedOut} range={range} onRange={setRange} pollZones={polling.zones} sport={sport} />}
        <div className={styles.lower}>
          <LatestRides />
          <div className={styles.side}>
            {strava.isSuccess && <StravaImportCard strava={strava.data} />}
          </div>
        </div>
      </div>
    </>
  );
}
